import { useEffect, useMemo, useState } from "react";
import Pagination from "../common/pagination/pagination";
import Select, { components } from 'react-select';
import SearchInput from "../common/search-input/SearchInput";
import UserName from "../common/user/UserName";
import DatePicker from 'react-datepicker';
import styles from "./Concierge.module.css";
import CalendarIcon from "../../images/calendar-icon.svg";
import { useDebounce } from "../../utils/useDebounce";
import { getConcierge } from "../../redux/Concierge/actions/concierge.action";
import { AppDispatch, IRootState } from "../../redux/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../common/loader/Loader";
import EyeIcon1 from "../svgIcons/EyeIcon1";
import { parsePhoneNumber } from "awesome-phonenumber";
import { colourStyles } from "../locations/Locations";
import { getManagementCompany } from "../../redux/management-company/actions/management-company.action";
import { getStateLandmark } from "../../redux/landmark/actions/landmark.action";
import CustModal from "../common/custmodal/CustModal";
import ViewConcierge from "./viewConcierge/viewConcierge";
import TabGroup from "../tabGroup/TabGroup";
import { datePresetOptions } from "./constant"
import { getRangeForPreset } from "../../utils/helper";
import moment from "moment";
interface ConciergeLead {
    id: string;
    name: string;
    fullName: string;
    reservedDate: string;
    startTime: string;
    email: string;
    phoneNumber: string;
    property: string;
    managementCompany: string;
}

interface ManagementCompanyList {
    id: string;
    name: string;
    managementCompany: string;
    label: string;
    value: string;
}

interface LocationListInterface {
    locationName: string;
    id: string;
}

interface FormattedConciergeList {
    id: string;
    leadName: string;
    date: string;
    time: string;
    email: string;
    phone: string;
    property: string;
    managementGroup: string;
}
const SHOW_ALL_PROPERTY = { label: 'All Properties', value: '' };
const SHOW_ALL_MANAGEMENT = { label: 'All Management Companies', value: '' };

export default function Concierge() {
    const dispatch: AppDispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedManagementGroup, setSelectedManagementGroup] = useState<{ label: string; value: string }>(SHOW_ALL_MANAGEMENT);
    const [selectedProperty, setSelectedProperty] = useState<{ label: string; value: string }>(SHOW_ALL_PROPERTY);
    const searchWithDebounce = useDebounce(searchTerm, 1000, setPageNumber);
    const pageLimit = 10;
    const [datePreset, setDatePreset] = useState<string>('This week');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getRangeForPreset('This week'));
    const [startDate, endDate] = dateRange;
    const [selectedConcierge, setSelectedConcierge] = useState<typeof formattedConciergeList[0] | null>(null);
    const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
    const { data: conciergeList, loading: conciergeListLoading } = useSelector((state: IRootState) => state.conciergeReducer);
    const { data: managementCompanyList } = useSelector((state: IRootState) => state.managementCompanyReducer);
    const { data: locationList } = useSelector((state: IRootState) => state.landmarkReducer);
    const allLocations = (locationList)?.allLocations ?? [];

    useEffect(() => {
        dispatch(getManagementCompany());
    }, []);

    const shouldCallApiWithDates = !startDate || (startDate && endDate);

    useEffect(() => {
        if (!shouldCallApiWithDates) return;

        dispatch(
            getConcierge({
                leadName: searchWithDebounce,
                startDate: formatDateForApi(startDate),
                endDate: formatDateForApi(endDate),
                propertyName: selectedProperty.value || undefined,
                groups: selectedManagementGroup.value,
                page: pageNumber,
                limit: pageLimit,
                setTotalCount
            })
        );
    }, [searchWithDebounce, startDate, endDate, selectedProperty.value, selectedProperty.label, selectedManagementGroup.value, pageNumber, shouldCallApiWithDates]);

    useEffect(() => {
        dispatch(getStateLandmark(''));
    }, []);

    const localColourStyles = {
        ...colourStyles,
        control: (base: any) => ({
            ...colourStyles.control(base),
            borderRadius: '8px',
        }),
        option: (styles: any, state: any) => ({
            ...colourStyles.option(styles, state),
            wordBreak: 'break-word' as const,
            whiteSpace: 'normal' as const,
            padding: '10px 15px',
        }),
        menu: (base: any) => ({
            ...base,
            zIndex: 100,
        }),
    };

    const CustomOption = (props: any) => {
        return (
            <components.Option {...props}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{props.label}</span>
                </div>
            </components.Option>
        );
    };

    const propertyOptions = useMemo(() => {
        const options = [SHOW_ALL_PROPERTY];
        if (!Array.isArray(allLocations) || allLocations.length === 0) return options;
        allLocations.forEach((loc: LocationListInterface) => {
            const label = loc?.locationName?.replace(/"/g, "")?.trim() || "-";
            const value = loc?.id || "";
            if (label && value && label !== "-") {
                options.push({ label, value });
            }
        });
        return options;
    }, [allLocations]);

    const managementCompanyOptions = useMemo(() => {
        const options = [SHOW_ALL_MANAGEMENT];
        if (!managementCompanyList || !Array.isArray(managementCompanyList)) return options;
        managementCompanyList.forEach((item: ManagementCompanyList) => {
            const label = typeof item === 'string' ? item : (item?.name ?? item?.managementCompany ?? item?.label ?? '');
            const value = typeof item === 'string' ? item : (item?.id ?? item?.name ?? item?.managementCompany ?? item?.value ?? '');
            if (label && value && !options.some((o) => o.value === value)) {
                options.push({ label, value });
            }
        });
        return options;
    }, [managementCompanyList]);

    const handleCalendarClose = () => {
        if (startDate && !endDate) {
            setDateRange([startDate, startDate]);
            setPageNumber(1);
        }
    };

    const formatPhoneNumber = (phone: string) => {
        if (!phone || phone === "-") return "-";

        try {
            const phoneNumber = parsePhoneNumber(`+${phone}`);
            if (phoneNumber && phoneNumber.valid) {
                return phoneNumber.number.international;
            }
            return phone; // Return original if parsing fails
        } catch (error) {
            console.warn('Phone number parsing failed:', error);
            return phone; // Return original if parsing fails
        }
    };
    const formattedConciergeList = useMemo(() => {
        return conciergeList?.map((item: ConciergeLead) => ({
            id: item.id,
            leadName: item.fullName || "-",
            date: item.reservedDate
                ? moment.utc(item.reservedDate).format("MM-DD-YYYY")
                : "-",
            time: item.startTime
                ? moment(item.startTime, ["hh:mm A", "h:mm A", "HH:mm"], true).isValid()
                    ? moment(item.startTime, ["hh:mm A", "h:mm A", "HH:mm"]).format("hh:mm A")
                    : item.startTime
                : "-",
            email: item.email || "-",
            phone: item.phoneNumber ? formatPhoneNumber(item.phoneNumber) : "-",
            property: item.name?.replace(/"/g, "") || "-",
            managementGroup: item.managementCompany || "-"
        }));
    }, [conciergeList]);

    const formatDateForApi = (date: Date | null) => {
        if (!date) return undefined;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return (
        <>
            {conciergeListLoading && shouldCallApiWithDates && <Loader />}
            <CustModal
                open={showViewDetailsModal}
                setOpen={setShowViewDetailsModal}
                bodyData={
                    selectedConcierge && (
                        <ViewConcierge
                            setOpen={setShowViewDetailsModal}
                            conciergeDataList={selectedConcierge}
                        />
                    )
                }
            />
            <div className={styles.conciergePageContainer}>
                {/* HEADER */}
                <div className={styles.conciergePageHeader}>
                    <div className={styles.conciergeLeftContainer}>
                        <p className={styles.conciergeUserGreet}>
                            Welcome, <UserName />
                        </p>
                        <h3>All Scheduled Tours</h3>
                    </div>

                    <div className={styles.rightContainer}>
                        <div className={styles.searchFilterWrapper}>
                            <SearchInput
                                value={searchTerm}
                                placeholder={"Search Lead by Name"}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: '100%', minWidth: 0 }}
                            />
                        </div>

                        <div className={styles.filterDropDown}>
                            <Select
                                onChange={(selectedOption) => {
                                    setSelectedProperty(selectedOption ?? SHOW_ALL_PROPERTY);
                                    setPageNumber(1);
                                }}
                                isSearchable={true}
                                classNamePrefix="custom-react-select"
                                value={
                                    selectedProperty.value !== ''
                                        ? { value: selectedProperty.value, label: selectedProperty.label }
                                        : SHOW_ALL_PROPERTY
                                }
                                options={propertyOptions}
                                components={{ Option: CustomOption }}
                                placeholder="Property"
                                styles={localColourStyles}
                            />
                        </div>

                        <div className={styles.filterDropDown}>
                            <Select
                                onChange={(selectedOption) => {
                                    setSelectedManagementGroup(selectedOption ?? SHOW_ALL_MANAGEMENT);
                                    setPageNumber(1);
                                }}
                                isSearchable={true}
                                classNamePrefix="custom-react-select"
                                value={
                                    selectedManagementGroup.value !== ''
                                        ? { value: selectedManagementGroup.value, label: selectedManagementGroup.label }
                                        : SHOW_ALL_MANAGEMENT
                                }
                                components={{ Option: CustomOption }}
                                options={managementCompanyOptions}
                                placeholder="Management Company"
                                styles={localColourStyles}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.dateFilterGroup}>
                    {/* range selector */}
                    <div className={styles.filterTabs}>
                        <TabGroup
                            options={datePresetOptions}
                            value={datePreset}
                            onChange={(val) => {
                                const preset = String(val);

                                // Prevent unnecessary updates if same preset is clicked
                                if (preset === datePreset) return;
                                setDatePreset(preset);

                                // For presets, immediately set the corresponding date range
                                // so the DatePicker reflects the selected filter.
                                if (preset !== 'Custom') {
                                    const [presetStart, presetEnd] = getRangeForPreset(preset);
                                    setDateRange([presetStart, presetEnd]);
                                }
                            }}
                        />
                    </div>

                    {/* date picker */}
                    <div className={`${styles.datePickerWrapper} datePicker-wrapper`}>
                        <DatePicker
                            selected={startDate}
                            popperPlacement="bottom-start"
                            dateFormat="MM/dd/yyyy"
                            showPopperArrow={false}
                            // timeIntervals={30}
                            placeholderText="Start Date - End Date"
                            name="Start Date"
                            className="custom-dateInput"
                            // disabled={datePreset !== 'Custom'}
                            isClearable={false}
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            // maxDate={today}
                            onChange={(e) => {
                                setDatePreset('Custom');
                                setDateRange(e);
                                if (e?.[0] && e?.[1]) setPageNumber(1);
                            }}
                            onCalendarClose={handleCalendarClose}
                        />
                        <img src={CalendarIcon} alt="calendar" />
                    </div>
                </div>

                {/* TABLE */}
                <div className={styles.conciergeTableContainer}>
                    <div className={styles.conciergeTableListing}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Lead Name</th>
                                    <th>Appointment Date & Time</th>
                                    <th>Email</th>
                                    <th>Phone Number</th>
                                    <th>Property</th>
                                    <th>Management Group</th>
                                    {/* <th>Action</th> */}
                                </tr>
                            </thead>

                            <tbody>
                                {formattedConciergeList?.length > 0 ? (
                                    formattedConciergeList.map((item: FormattedConciergeList, index: number) => {
                                        return (
                                            <tr key={item.id}>
                                                <td>{item.leadName}</td>
                                                <td>
                                                    <div className={styles.dateTimeColumn}>
                                                        <div className={styles.dateRow}>{item.date}</div>
                                                        <div className={styles.timeRow}>{item.time}</div>
                                                    </div>
                                                </td>
                                                <td>{item.email}</td>
                                                <td>{item.phone}</td>
                                                <td>{item.property}</td>
                                                <td>{item.managementGroup}</td>
                                                {/* <td>
                                                    <div className={styles.actionButton}
                                                        role="button" onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                            event.stopPropagation();
                                                            setSelectedConcierge(conciergeList[index]);
                                                            setShowViewDetailsModal(true);

                                                        }} >
                                                        <EyeIcon1 width={24} height={24} />
                                                    </div>
                                                </td> */}
                                            </tr>
                                        );
                                    })
                                ) : (
                                    !formattedConciergeList?.length && conciergeListLoading === false &&
                                    <tr>
                                        <td className={styles.noData} colSpan={7}>
                                            No Data Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    <Pagination
                        className="pagination-bar"
                        currentPage={pageNumber}
                        totalCount={Math.ceil(totalCount / pageLimit)}
                        pageSize={1}
                        onPageChange={(page: number) => setPageNumber(page)}
                    />
                </div>
            </div>
        </>
    );
}